import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  enableReporting?: boolean;
  reportingEndpoint?: string;
  retryable?: boolean;
  isolateError?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  isRetrying: boolean;
  errorId: string;
  userFriendlyMessage: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

const ErrorContainer = styled(motion.div)<{
  $severity: State['severity'];
  $isolateError: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: ${({ theme }) => theme.spacing.xl};
  margin: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  text-align: center;
  position: relative;
  
  ${({ $isolateError }) =>
    $isolateError &&
    css`
      border: 2px dashed ${({ theme }) => theme.colors.error}40;
      background-color: ${({ theme }) => theme.colors.error}05;
    `}
  
  ${({ $severity, theme }) => {
    switch ($severity) {
      case 'critical':
        return css`
          background: linear-gradient(135deg, ${theme.colors.error}10 0%, ${theme.colors.error}20 100%);
          border: 2px solid ${theme.colors.error}40;
        `;
      case 'high':
        return css`
          background: linear-gradient(135deg, ${theme.colors.warning}10 0%, ${theme.colors.warning}20 100%);
          border: 2px solid ${theme.colors.warning}40;
        `;
      case 'medium':
        return css`
          background: linear-gradient(135deg, ${theme.colors.info}10 0%, ${theme.colors.info}20 100%);
          border: 2px solid ${theme.colors.info}40;
        `;
      default:
        return css`
          background-color: ${theme.colors.gray.light};
          border: 2px solid ${theme.colors.gray.medium};
        `;
    }
  }}
`;

const ErrorIcon = styled.div<{ $severity: State['severity'] }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  
  ${({ $severity, theme }) => {
    switch ($severity) {
      case 'critical':
        return css`
          background-color: ${theme.colors.error};
          color: white;
        `;
      case 'high':
        return css`
          background-color: ${theme.colors.warning};
          color: white;
        `;
      case 'medium':
        return css`
          background-color: ${theme.colors.info};
          color: white;
        `;
      default:
        return css`
          background-color: ${theme.colors.gray.medium};
          color: white;
        `;
    }
  }}
`;

const ErrorTitle = styled.h2`
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

const ErrorMessage = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.gray.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  line-height: 1.5;
  max-width: 400px;
`;

const ErrorDetails = styled.details`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.gray.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  max-width: 100%;
  
  summary {
    cursor: pointer;
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.gray.dark};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
  
  pre {
    background-color: ${({ theme }) => theme.colors.gray.medium};
    padding: ${({ theme }) => theme.spacing.sm};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  justify-content: center;
`;

const ActionButton = styled.button<{
  $variant: 'primary' | 'secondary' | 'danger';
  $disabled?: boolean;
}>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  min-width: 100px;
  
  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.6;
    `}
  
  ${({ $variant, theme, $disabled }) => {
    if ($disabled) return '';
    
    switch ($variant) {
      case 'primary':
        return css`
          background-color: ${theme.colors.primary};
          color: white;
          
          &:hover {
            background-color: ${theme.colors.primaryDark};
            transform: translateY(-1px);
          }
        `;
      case 'secondary':
        return css`
          background-color: ${theme.colors.gray.light};
          color: ${theme.colors.text};
          border: 1px solid ${theme.colors.gray.medium};
          
          &:hover {
            background-color: ${theme.colors.gray.medium};
            transform: translateY(-1px);
          }
        `;
      case 'danger':
        return css`
          background-color: ${theme.colors.error};
          color: white;
          
          &:hover {
            background-color: ${theme.colors.errorDark};
            transform: translateY(-1px);
          }
        `;
    }
  }}
`;

const LoadingOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
  backdrop-filter: blur(2px);
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${({ theme }) => theme.colors.gray.light};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorId = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.gray.dark};
  background-color: ${({ theme }) => theme.colors.gray.light};
  padding: 4px 8px;
  border-radius: 12px;
  font-family: monospace;
`;

class ProductionErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
      errorId: '',
      userFriendlyMessage: '',
      severity: 'medium',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `ERR-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 5)}`;
    const { userFriendlyMessage, severity } = ProductionErrorBoundary.categorizeError(error);
    
    return {
      hasError: true,
      error,
      errorId,
      userFriendlyMessage,
      severity,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      errorInfo,
    });

    // Report error
    if (this.props.enableReporting) {
      this.reportError();
    }

    // Call custom error handler
    this.props.onError?.(error, errorInfo);

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  private static categorizeError(error: Error): {
    userFriendlyMessage: string;
    severity: State['severity'];
  } {
    const errorMessage = error.message.toLowerCase();
    const errorStack = error.stack?.toLowerCase() || '';

    // Critical errors
    if (
      errorMessage.includes('network error') ||
      errorMessage.includes('failed to fetch') ||
      errorMessage.includes('loading chunk') ||
      errorMessage.includes('dynamically imported module')
    ) {
      return {
        userFriendlyMessage: 'We\'re having trouble loading some content. Please check your internet connection and try again.',
        severity: 'high',
      };
    }

    // Authentication errors
    if (
      errorMessage.includes('unauthorized') ||
      errorMessage.includes('forbidden') ||
      errorMessage.includes('authentication')
    ) {
      return {
        userFriendlyMessage: 'Your session may have expired. Please refresh the page and try logging in again.',
        severity: 'medium',
      };
    }

    // Database/API errors
    if (
      errorMessage.includes('internal server error') ||
      errorMessage.includes('service unavailable') ||
      errorMessage.includes('timeout')
    ) {
      return {
        userFriendlyMessage: 'Our servers are experiencing some issues. We\'re working to fix this. Please try again in a few minutes.',
        severity: 'high',
      };
    }

    // React/Component errors
    if (
      errorStack.includes('react') ||
      errorMessage.includes('cannot read propert') ||
      errorMessage.includes('undefined is not a function')
    ) {
      return {
        userFriendlyMessage: 'Something went wrong with this part of the page. Try refreshing or contact support if the problem continues.',
        severity: 'medium',
      };
    }

    // Generic fallback
    return {
      userFriendlyMessage: 'An unexpected error occurred. We\'ve been notified and are working on a fix.',
      severity: 'low',
    };
  }

  private async reportError() {
    // Completely disable error reporting
    return;
  }

  private handleRetry = async () => {
    if (this.state.retryCount >= 3) return;

    this.setState({ isRetrying: true });

    // Simulate delay for better UX
    await new Promise(resolve => setTimeout(resolve, 1000));

    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
      isRetrying: false,
    }));
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleReportIssue = () => {
    const subject = encodeURIComponent(`Error Report: ${this.state.errorId}`);
    const body = encodeURIComponent(
      `Error ID: ${this.state.errorId}\n` +
      `URL: ${window.location.href}\n` +
      `Message: ${this.state.userFriendlyMessage}\n` +
      `Browser: ${navigator.userAgent}\n\n` +
      `Please describe what you were doing when this error occurred:`
    );
    
    window.open(`mailto:support@example.com?subject=${subject}&body=${body}`);
  };

  private getErrorIcon() {
    switch (this.state.severity) {
      case 'critical':
        return '🔥';
      case 'high':
        return '⚠️';
      case 'medium':
        return '❗';
      default:
        return '🤔';
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const canRetry = this.props.retryable !== false && this.state.retryCount < 3;
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <ErrorContainer
          $severity={this.state.severity}
          $isolateError={this.props.isolateError || false}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <ErrorId>{this.state.errorId}</ErrorId>
          
          <ErrorIcon $severity={this.state.severity}>
            {this.getErrorIcon()}
          </ErrorIcon>
          
          <ErrorTitle>Oops! Something went wrong</ErrorTitle>
          
          <ErrorMessage>
            {this.state.userFriendlyMessage}
          </ErrorMessage>

          <ActionButtons>
            {canRetry && (
              <ActionButton
                $variant="primary"
                $disabled={this.state.isRetrying}
                onClick={this.handleRetry}
              >
                {this.state.isRetrying ? 'Retrying...' : `Try Again (${3 - this.state.retryCount} left)`}
              </ActionButton>
            )}
            
            <ActionButton
              $variant="secondary"
              onClick={this.handleReload}
            >
              Refresh Page
            </ActionButton>
            
            <ActionButton
              $variant="secondary"
              onClick={this.handleReportIssue}
            >
              Report Issue
            </ActionButton>
          </ActionButtons>

          {isDevelopment && this.state.error && (
            <ErrorDetails>
              <summary>Technical Details (Development Only)</summary>
              <pre>
                <strong>Error:</strong> {this.state.error.message}
                {'\n\n'}
                <strong>Stack:</strong> {this.state.error.stack}
                {'\n\n'}
                <strong>Component Stack:</strong> {this.state.errorInfo?.componentStack}
              </pre>
            </ErrorDetails>
          )}

          <AnimatePresence>
            {this.state.isRetrying && (
              <LoadingOverlay
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <LoadingSpinner />
              </LoadingOverlay>
            )}
          </AnimatePresence>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ProductionErrorBoundary;
